/**
 * Hook for fetching child assets discovered from the current asset
 *
 * Queries for assets that were discovered by the current asset through various
 * relationship types (DISCOVERED, HAS_ATTRIBUTE, HAS_WEBPAGE).
 */

import { useGraphQuery } from '@/hooks/useGraphQuery';
import { TreeNode } from '@/types';

export interface UseChildAssetsQueryResult {
  /** Child assets tree structure from graph query */
  data: TreeNode[] | undefined;
  /** True if query is in initial loading state */
  isLoading: boolean;
  /** Query status */
  status: 'pending' | 'error' | 'success';
}

/**
 * Custom hook for fetching child assets discovered by the current asset
 *
 * Traverses the graph to find assets discovered through:
 * - DISCOVERED relationship (direct discovery)
 * - HAS_ATTRIBUTE relationship (attribute-based discovery)
 * - HAS_WEBPAGE relationship (web page discovery)
 *
 * Uses 2-hop relationship traversal with source filter to find children.
 * The query pattern is: Asset -> [relationship] -> Intermediate -> [relationship] -> Child Asset
 *
 * @param assetKey - The asset key to find children for
 * @returns Child assets tree data with loading state
 */
export function useChildAssetsQuery(
  assetKey: string
): UseChildAssetsQueryResult {
  const { data, isLoading, status } = useGraphQuery({
    tree: true,
    query: {
      node: {
        labels: ['Asset'],
        relationships: [
          {
            label: ['DISCOVERED', 'HAS_ATTRIBUTE', 'HAS_WEBPAGE'],
            length: 2,
            source: {
              labels: ['Asset'],
              filters: [
                {
                  field: 'key',
                  operator: '=',
                  value: assetKey,
                },
              ],
            },
          },
        ],
      },
    },
  });

  return {
    data,
    isLoading,
    status,
  };
}
